import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import glassTheme from '../../styles/glassTheme';
import * as teacherApi from '../../services/teacherApi';
import type { Course } from '../../services/teacherApi';
import { useAuthStore } from '../../store/authStore';
import { DashboardLayout, Card } from '../../components/DashboardLayout';

const CourseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const CourseCard = styled.div`
  background: ${glassTheme.glass.container};
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.lg};
  padding: 1.5rem;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};

  &:hover {
    border-color: ${glassTheme.colors.borderHover};
    transform: translateY(-4px);
    box-shadow: ${glassTheme.shadow.xl};
  }
`;

const CourseCardMain = styled.div`
  cursor: pointer;
`;

const CourseCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 1rem;
`;

const CourseTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: ${glassTheme.colors.text.primary};
`;

const CourseCode = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  color: ${glassTheme.colors.accent.blue};
  background: rgba(59, 130, 246, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: ${glassTheme.borderRadius.sm};
`;

const PublishedBadge = styled.span<{ $published: boolean }>`
  padding: 0.25rem 0.75rem;
  border-radius: ${glassTheme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => props.$published
    ? 'rgba(34, 197, 94, 0.2)'
    : 'rgba(251, 191, 36, 0.2)'};
  color: ${props => props.$published
    ? '#22c55e'
    : '#f59e0b'};
`;

const CourseDescription = styled.p`
  color: ${glassTheme.colors.text.secondary};
  font-size: 0.875rem;
  margin: 0 0 1rem 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CourseStats = styled.div`
  display: flex;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.muted};
  font-size: 0.875rem;
`;

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ReplicateButton = styled.button`
  margin-left: auto;
  padding: 0.3rem 0.65rem;
  background: rgba(96, 165, 250, 0.12);
  border: 1px solid rgba(96, 165, 250, 0.35);
  border-radius: ${glassTheme.borderRadius.sm};
  color: ${glassTheme.colors.accent.blue};
  font-size: 0.8rem;
  font-family: inherit;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};

  &:hover:not(:disabled) {
    background: rgba(96, 165, 250, 0.22);
    border-color: ${glassTheme.colors.accent.blue};
  }

  &:disabled {
    cursor: wait;
    opacity: 0.6;
  }
`;

const StudentLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: ${glassTheme.colors.accent.blue};
  cursor: pointer;
  transition: color 0.2s;
  background: none;
  border: none;
  padding: 0;
  font-size: inherit;
  font-family: inherit;
  text-decoration: none;

  &:hover {
    color: ${glassTheme.colors.accent.cyan};
    text-decoration: underline;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const CreateButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${glassTheme.gradients.cyber};
  border: none;
  border-radius: ${glassTheme.borderRadius.md};
  color: ${glassTheme.colors.text.primary};
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${glassTheme.shadow.glow};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: ${glassTheme.colors.text.primary};
`;

const EmptyStateText = styled.p`
  color: ${glassTheme.colors.text.secondary};
  margin-bottom: 2rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: ${glassTheme.colors.text.secondary};
`;

export const CourseListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [replicatingCourseId, setReplicatingCourseId] = useState<string | null>(null);

  useEffect(() => {
    if (user?.teacherInfo?.approvalStatus !== 'approved') {
      toast.error('您的教师账号尚未通过审核');
      navigate('/teacher/dashboard');
      return;
    }
    loadCourses();
  }, [user, navigate]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const data = await teacherApi.getCourses({ teacher: 'me' });
      setCourses(data);
    } catch (error) {
      console.error('Failed to load courses:', error);
      toast.error('加载课程失败');
    } finally {
      setLoading(false);
    }
  };

  const handleReplicate = async (course: Course) => {
    try {
      setReplicatingCourseId(course._id);
      const replicatedCourse = await teacherApi.replicateCourse(course._id);
      toast.success(`课程“${course.title}”复制成功`);
      navigate(`/teacher/courses/${replicatedCourse._id}`);
    } catch (error: any) {
      console.error('Failed to replicate course:', error);
      toast.error(error.response?.data?.message || '复制课程失败');
    } finally {
      setReplicatingCourseId(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="课程管理" subtitle="管理您的课程">
        <LoadingSpinner>加载中...</LoadingSpinner>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="课程管理" subtitle="创建和管理您的课程">
      <Header>
        <div style={{ fontSize: '1.5rem', fontWeight: 600, color: glassTheme.colors.text.primary }}>
          我的课程 ({courses.length})
        </div>
        <CreateButton onClick={() => navigate('/teacher/courses/new')}>
          <span>➕</span>
          <span>创建课程</span>
        </CreateButton>
      </Header>

      {courses.length === 0 ? (
        <Card>
          <EmptyState>
            <EmptyStateIcon>📚</EmptyStateIcon>
            <EmptyStateTitle>还没有课程</EmptyStateTitle>
            <EmptyStateText>创建您的第一个课程开始教学</EmptyStateText>
            <CreateButton onClick={() => navigate('/teacher/courses/new')}>
              <span>➕</span>
              <span>创建课程</span>
            </CreateButton>
          </EmptyState>
        </Card>
      ) : (
        <CourseGrid>
          {courses.map(course => (
            <CourseCard key={course._id}>
              <CourseCardMain onClick={() => navigate(`/teacher/courses/${course._id}`)}>
                <CourseCardHeader>
                  <div style={{ flex: 1 }}>
                    <CourseTitle>{course.title}</CourseTitle>
                    <CourseCode>{course.courseCode}</CourseCode>
                  </div>
                  <PublishedBadge $published={course.isPublished}>
                    {course.isPublished ? '已发布' : '草稿'}
                  </PublishedBadge>
                </CourseCardHeader>

                <CourseDescription>{course.description}</CourseDescription>
              </CourseCardMain>

              <CourseStats>
                <StudentLink
                  href={`/teacher/courses/${course._id}/students`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  👥 {Array.isArray(course.enrolledStudents) ? course.enrolledStudents.length : 0} 学生
                </StudentLink>
                <StatItem>📝 {course.totalTopics || 0} 知识元</StatItem>
                <ReplicateButton
                  type="button"
                  onClick={() => handleReplicate(course)}
                  disabled={replicatingCourseId !== null}
                  aria-label={`复制课程 ${course.title}`}
                >
                  {replicatingCourseId === course._id ? '复制中...' : '📋 复制'}
                </ReplicateButton>
              </CourseStats>
            </CourseCard>
          ))}
        </CourseGrid>
      )}
    </DashboardLayout>
  );
};
